import { DeviceEvent } from "../models/DeviceEvent";
import { DeviceEventCriteria, DeviceEventCriteriaHelper } from "../models/DeviceEventCriteria";
import OCGatewayClient from "../client/OCGatewayClient";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { gatewayRealtimeQueryOptions, prependInfiniteQueryItem, updateMatchingQueries } from "./gatewayQueryUtils";

export interface IDevice {
    deviceId: string;
    description?: string;
}

let subscribed = false;

export function useDeviceEventsQuery(deviceId: string, criteria?: DeviceEventCriteria, limit = 50, enabled = true) {
    const queryClient = useQueryClient();

    const query = useInfiniteQuery({
        queryKey: ['getDeviceEvents', deviceId, criteria, limit],
        enabled,
        queryFn: async ({pageParam : beforeGlobalId}): Promise<DeviceEvent[]> => {
            const requestParams: any = {
                deviceId,
                limit,
                subscribe: !beforeGlobalId,
                // Spread all criteria properties, filtering out undefined values
                ...Object.fromEntries(
                    Object.entries(criteria || {}).filter(([_, value]) => value !== undefined)
                )
            };

            if (beforeGlobalId) {
                requestParams.beforeGlobalId = beforeGlobalId;
            }

            return OCGatewayClient.request('getDeviceEvents', requestParams) as Promise<DeviceEvent[]>;
        },
        ...gatewayRealtimeQueryOptions,
        getNextPageParam: (lastPage) => {
            if (lastPage.length < limit) {
                return undefined;
            }

            return lastPage.length ? lastPage[lastPage.length - 1].globalId : undefined;
        },
        initialPageParam: undefined as number | undefined
    });

    if(!subscribed) {
        OCGatewayClient.socket.on('deviceEventUpdate', (data: any) => {
            // Update queries for this deviceId, checking criteria for each query
            updateMatchingQueries(queryClient, ['getDeviceEvents', data.deviceId], (queryKey) => {
                const queryCriteria = queryKey[2] as DeviceEventCriteria | undefined;

                queryClient.setQueryData(queryKey, (queryData: any) => {
                    const pages: Array<DeviceEvent[]> = queryData?.pages;

                    if(!pages?.length) {
                        console.log('deviceEventUpdate (ignored)', data);
                        return queryData;
                    }

                    if(data.type === 'create')
                    {
                        // Check if the new event matches this query's criteria
                        if (DeviceEventCriteriaHelper.eventMatches(data.body, queryCriteria)) {
                            return prependInfiniteQueryItem<DeviceEvent>(queryData, data.body, 'deviceEventUpdate');
                        } else {
                            console.log('deviceEventUpdate (filtered out)', data);
                            return queryData;
                        }
                    }

                    console.log('deviceEventUpdate (no match)', data);
                    return queryData;
                });
            });
        });

        subscribed = true;
    }

    return query;
}
